var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { differenceInDays, differenceInMonths, differenceInWeeks } from "date-fns";
import { isErr } from "healthex-common/result";
import { pluralize } from "healthex-common/utils";
import { t } from "i18next";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { validators } from "../Logic/Validators";
import { FormModel } from "./FormModel";
export class AccountSettingsLoginAndSecurityViewModel {
    constructor(container) {
        this.formModel = new FormModel({
            currentPassword: "",
            newPassword: "",
            reEnteredNewPassword: "",
        }, ["currentPassword", "newPassword", "reEnteredNewPassword"]);
        this.editingPassword = false;
        this.authManager = container.authManager;
        this.organizationsManager = container.organizationsManager;
        this.passwordValidator = container.passwordValidator;
    }
    get myOrganization() {
        return this.organizationsManager.myOrganization.value;
    }
    get organizationPasswordConfiguration() {
        var _a;
        if (this.myOrganization === NOT_LOADED)
            return null;
        return (_a = this.myOrganization) === null || _a === void 0 ? void 0 : _a.passwordConfiguration;
    }
    get passwordSectionText() {
        if (this.authManager.passwordUpdatedAt === null)
            return null;
        const passwordUpdatedAt = new Date(this.authManager.passwordUpdatedAt);
        const daysSinceLastUpdate = Math.max(0, differenceInDays(new Date(), passwordUpdatedAt));
        if (daysSinceLastUpdate === 0) {
            return `Last updated less than a day ago`;
        }
        else if (daysSinceLastUpdate <= 6) {
            return `Last updated ${daysSinceLastUpdate} ${pluralize(daysSinceLastUpdate, "day", "days")} ago`;
        }
        else if (daysSinceLastUpdate <= 27) {
            const weeksSinceLastUpdate = Math.max(1, differenceInWeeks(new Date(), passwordUpdatedAt));
            return `Last updated ${weeksSinceLastUpdate} ${pluralize(weeksSinceLastUpdate, "week", "weeks")} ago`;
        }
        const monthsSinceLastUpdate = Math.max(1, differenceInMonths(new Date(), passwordUpdatedAt));
        return `Last updated ${monthsSinceLastUpdate} ${pluralize(monthsSinceLastUpdate, "month", "months")} ago`;
    }
    get showPasswordSection() {
        if (this.myOrganization === NOT_LOADED || this.myOrganization === null)
            return false;
        return !this.myOrganization.ssoEnabled;
    }
    toggleEditingPassword() {
        this.editingPassword = !this.editingPassword;
        if (!this.editingPassword) {
            this.clearPasswordInput();
        }
    }
    clearPasswordInput() {
        this.formModel.input = { currentPassword: "", newPassword: "", reEnteredNewPassword: "" };
    }
    validateInput() {
        const { notBlank } = validators;
        const f = this.formModel;
        f.clearErrors();
        const validationResults = this.passwordValidator.validateMultipleErrors({
            currentPassword: this.formModel.input.currentPassword,
            newPassword: this.formModel.input.newPassword,
            reEnteredNewPassword: this.formModel.input.reEnteredNewPassword || null,
            newPasswordHashed: null,
            currentPasswordHashed: null,
            passwordConfiguration: this.organizationPasswordConfiguration,
        });
        f.validateField("currentPassword", notBlank, "Enter your current password.");
        f.validateField("newPassword", notBlank, "Enter your new password.");
        f.validateField("reEnteredNewPassword", notBlank, "Enter your new password confirmation.");
        if (validationResults.includes("OLD_PASSWORD_REUSED")) {
            f.addError("newPassword", "Cannot reuse your current password.");
        }
        if (validationResults.includes("PASSWORD_CRITERIA_MISMATCH")) {
            f.addError("newPassword", "Password doesn't meet requirements. Please try again.");
        }
        if (validationResults.includes("PASSWORD_MISMATCH")) {
            f.addError("reEnteredNewPassword", "Passwords don't match. Please try again.");
        }
        return !f.hasErrors();
    }
    async savePassword() {
        if (!this.authManager.email)
            return;
        this.validateInput();
        const result = await this.authManager.changePassword({
            email: this.authManager.email,
            currentPassword: this.formModel.input.currentPassword,
            password: this.formModel.input.newPassword,
            reEnteredNewPassword: this.formModel.input.reEnteredNewPassword,
        });
        if (isErr(result)) {
            switch (result.error) {
                case "INCORRECT_CREDENTIALS":
                    this.formModel.addError("currentPassword", "Password is incorrect. Please try again.");
                    break;
                case "ACCOUNT_LOCKED":
                    this.formModel.addFormError(t("Your account is temporarily locked to prevent unauthorized use. You can either reset your password or try again later. If you still have trouble, contact your administrator."));
                    break;
                case "OLD_PASSWORD_REUSED":
                    this.formModel.addError("newPassword", "Cannot reuse your current password.");
                    break;
                default:
                    break;
            }
        }
        else {
            await this.authManager.refreshTokenInfo();
            this.editingPassword = false;
            this.clearPasswordInput();
        }
    }
}
__decorate([
    observable
], AccountSettingsLoginAndSecurityViewModel.prototype, "editingPassword", void 0);
__decorate([
    computed
], AccountSettingsLoginAndSecurityViewModel.prototype, "myOrganization", null);
__decorate([
    computed
], AccountSettingsLoginAndSecurityViewModel.prototype, "organizationPasswordConfiguration", null);
__decorate([
    computed
], AccountSettingsLoginAndSecurityViewModel.prototype, "passwordSectionText", null);
__decorate([
    computed
], AccountSettingsLoginAndSecurityViewModel.prototype, "showPasswordSection", null);
